import DefaultAsyncErrorFallback from '@/components/fallback/default-fallback';
import TimeoutFallback from '@/components/fallback/timeout';
import { Component, ReactNode, createContext, useContext } from 'react';

interface AsyncErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error) => void;
    onReset?: () => Promise<void>;
    onCancel?: () => void;
    showCancelButton?: boolean;
}

interface AsyncErrorBoundaryState {
    hasError: boolean;
    error:
        | (Error & { status?: number; code?: string; errorCode?: string })
        | null;
    isResetting: boolean;
    isDismissed: boolean;
}

// Create context outside the component
interface AsyncErrorContextType {
    captureError: (error: Error) => void;
}

const AsyncErrorContext = createContext<AsyncErrorContextType | null>(null);

export function useAsyncError() {
    const context = useContext(AsyncErrorContext);
    if (!context) {
        throw new Error('useAsyncError must be used within AsyncErrorBoundary');
    }
    return context;
}

export class AsyncErrorBoundary extends Component<
    AsyncErrorBoundaryProps,
    AsyncErrorBoundaryState
> {
    private captureErrorRef = (_: Error) => {};

    constructor(props: AsyncErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            isResetting: false,
            isDismissed: false,
        };

        // Bind the captureError method
        this.captureErrorRef = this.captureError.bind(this);
    }

    static getDerivedStateFromError(
        error: Error,
    ): Partial<AsyncErrorBoundaryState> {
        return {
            hasError: true,
            error,
            isDismissed: false,
        };
    }

    componentDidCatch(error: Error) {
        console.error('AsyncErrorBoundary caught error:', error);
        this.props.onError?.(error);
    }

    captureError = (error: Error) => {
        this.setState({
            hasError: true,
            error,
            isDismissed: false,
        });
        this.props.onError?.(error);
    };

    reset = async () => {
        this.setState({ isResetting: true });

        try {
            await this.props.onReset?.();
            this.setState({
                hasError: false,
                error: null,
                isResetting: false,
                isDismissed: false,
            });
        } catch (error) {
            console.error('Failed to reset:', error);
            this.setState({ isResetting: false });
        }
    };

    cancel = () => {
        this.setState({
            isDismissed: true,
        });
        this.props.onCancel?.();
    };

    render() {
        // If error is dismissed, render children (original content)
        if (this.state.isDismissed) {
            return (
                <AsyncErrorContext.Provider
                    value={{ captureError: this.captureErrorRef }}>
                    {this.props.children}
                </AsyncErrorContext.Provider>
            );
        }

        if (this.state.hasError) {
            if (
                this.state.error?.code === 'ECONNABORTED' ||
                this.state.error?.status === 500 ||
                (typeof this.state.error === 'string' &&
                    this.state.error === 'Network Error')
            ) {
                return (
                    <TimeoutFallback
                        error={this.state.error as Error & string}
                    />
                );
            }

            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <DefaultAsyncErrorFallback
                    error={this.state.error}
                    resetErrorBoundary={this.reset}
                    cancelErrorBoundary={this.cancel}
                    isResetting={this.state.isResetting}
                    showCancelButton={this.props.showCancelButton !== false}
                />
            );
        }

        // Normal state - provide context
        return (
            <AsyncErrorContext.Provider
                value={{ captureError: this.captureErrorRef }}>
                {this.props.children}
            </AsyncErrorContext.Provider>
        );
    }
}
